import { useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "@tellescope/ui";
import { VideoCallJoinCard } from "../../components/VideoCallJoinCard";

/**
 * Video call page for joining a Tellescope video call.
 * Maps to /video-call?calendarEventId={eventId}
 *
 * Flow:
 * 1. Fetch event using TanStack Query
 * 2. Show event details with "Join Video Call" button
 * 3. User clicks button to open the waiting room (VideoCallDialog)
 * 4. The waiting room connects once the provider starts the meeting
 */
export const VideoCallPage = () => {
  const { t } = useTranslation("videoCalling");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const calendarEventId = searchParams.get("calendarEventId") ?? "";

  // Fetch event using TanStack Query
  const {
    data: event,
    isLoading,
    error,
  } = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: calendarEventId,
    enabled: !!calendarEventId,
  });

  const handleBack = useCallback(() => {
    navigate("/");
  }, [navigate]);

  if (!calendarEventId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-sm font-medium text-destructive mb-1">{t("Missing event ID")}</p>
          <Button variant="outline" onClick={handleBack} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("Go Home")}
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading...")}</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <p className="text-sm font-medium text-destructive mb-1">{t("Unable to load event")}</p>
          <p className="text-xs text-muted-foreground mb-4">{error?.message ?? t("Event not found")}</p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("Go Home")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen">
      <VideoCallJoinCard event={event} />
    </div>
  );
};
